import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuthContext } from './useAuthContext';

/**
 * useCart Hook - Simplified Cart Management
 * 
 * Architecture:
 * - getOrCreateCart(): Gets active cart or creates new one
 * - fetchCartItems(): Fetches cart items with product details
 * - Explicit fetchCartItems() calls after modifications ensure UI updates
 * - No real-time subscriptions to avoid database trigger conflicts
 */
export const useCart = () => {
  const { session } = useAuthContext();
  const [cart, setCart] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch cart and items via RPC (ensures cart exists server-side)
  const fetchCartData = useCallback(async () => {
    if (!session) {
      throw new Error('You must be logged in to access cart');
    }

    console.log('📦 Fetching cart via RPC cart_get');
    const { data, error: rpcError } = await supabase.rpc('cart_get');
    if (rpcError) {
      console.error('Error fetching cart via RPC:', rpcError);
      throw rpcError;
    }

    const rawCart = data?.cart || null;
    const rawItems = data?.items || [];

    // Normalize numeric fields to numbers for safe toFixed usage in UI
    const nextCart = rawCart
      ? {
        ...rawCart,
        total: rawCart.total != null ? Number(rawCart.total) : 0,
        item_count: rawCart.item_count != null ? Number(rawCart.item_count) : 0,
      }
      : null;
    const nextItems = rawItems.map((it) => ({
      ...it,
      price: it.price != null ? Number(it.price) : 0,
      line_total: it.line_total != null ? Number(it.line_total) : undefined,
      quantity: it.quantity != null ? Number(it.quantity) : 0,
      variant_id: it.variant_id || null, // Preserve variant_id
    }));


    setCart(nextCart);
    setCartItems(nextItems);
    const count = nextItems.reduce((sum, item) => sum + item.quantity, 0);
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { count, cart: nextCart, items: nextItems } }));

    console.log('✅ Cart fetched via RPC:', {
      cartId: nextCart?.id,
      itemCount: nextItems.length,
    });

    return { cart: nextCart, items: nextItems };
  }, [session]);

  // Initialize cart on mount and when session changes
  useEffect(() => {
    const initializeCart = async () => {
      if (!session) {
        console.log('❌ No session, clearing cart');
        setCart(null);
        setCartItems([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const { cart: activeCart, items } = await fetchCartData();
        console.log('✅ Cart initialized (RPC):', { cart: activeCart, itemCount: items.length });
      } catch (err) {
        console.error('❌ Error initializing cart:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeCart();
  }, [session, fetchCartData]);

  // Add item to cart (with optional variant_id and note)
  const addToCart = async (productId, quantity = 1, variantId = null, note = null, customFields = null) => {
    if (!session) {
      throw new Error('You must be logged in to add items to cart');
    }

    try {
      console.log(`🛒 Adding to cart - Product: ${productId}, Variant: ${variantId}, Quantity: ${quantity}`);

      // Normalize numeric product IDs passed as strings to actual numbers (for bigint schemas)
      const normalizedProductId =
        typeof productId === 'string' && /^\d+$/.test(productId)
          ? Number(productId)
          : productId;

      // If variant_id is provided, use variant price but add product normally
      // Note: cart_items table doesn't have variant_id column, so we store variant info differently
      if (variantId) {
        // Get or create cart first
        const { data: cartData, error: cartError } = await supabase.rpc('cart_get');
        
        if (cartError) {
          console.error('❌ Cart RPC error:', cartError);
          throw new Error(`Failed to get cart: ${cartError.message}`);
        }
        
        console.log('📦 Cart RPC response:', cartData);
        
        // Handle both null and string "null" cases
        let activeCart = cartData?.cart;
        
        // If cart is null, try to create it manually
        if (!activeCart || activeCart === 'null' || activeCart === null) {
          console.log('⚠️ No cart found, creating new cart...');
          
          // Try to create a cart directly
          const { data: newCart, error: createError } = await supabase
            .from('carts')
            .insert({
              profile_id: session.user.id,
              status: 'active',
              total: 0,
              item_count: 0
            })
            .select()
            .single();
          
          if (createError) {
            console.error('❌ Failed to create cart:', createError);
            console.error('Cart data received:', cartData);
            console.error('Cart RPC response:', JSON.stringify(cartData, null, 2));
            throw new Error(`Failed to create cart: ${createError.message}`);
          }
          
          activeCart = newCart;
          console.log('✅ Cart created successfully:', activeCart.id);
        } else {
          console.log('✅ Cart retrieved successfully:', activeCart.id);
        }
        
        console.log('✅ Cart retrieved/created successfully:', activeCart.id);

        // Fetch variant details for price and stock, and product for brand_id and discount
        const { data: variantData, error: variantError } = await supabase
          .from('product_variants')
          .select('price, stock, product_id')
          .eq('id', variantId)
          .single();

        if (variantError) {
          throw new Error(`Variant not found: ${variantError.message}`);
        }

        if (variantData.stock < quantity) {
          throw new Error(`Only ${variantData.stock} items available in stock`);
        }

        // Fetch product to get brand_id and discount
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('brand_id, dis')
          .eq('id', normalizedProductId)
          .single();

        if (productError) {
          throw new Error(`Product not found: ${productError.message}`);
        }

        // Calculate discounted variant price if product has discount
        let finalPrice = parseFloat(variantData.price || 0);
        if (productData.dis && productData.dis !== 'no') {
          const discountPercent = parseFloat(productData.dis);
          finalPrice = variantData.price * (1 - discountPercent / 100);
          console.log(`💰 Variant discount applied: ${productData.dis}% → Price: ${variantData.price} → ${finalPrice.toFixed(2)}`);
        }

        // Check if item already exists in cart with same variant
        const { items: currentItems } = await fetchCartData();
        const existingItem = currentItems.find(item => 
          item.product_id === normalizedProductId && 
          (item.variant_id === variantId || (!item.variant_id && !variantId))
        );

        if (existingItem) {
          // Update quantity
          const { error: updateError } = await supabase.rpc('cart_update_quantity', {
            p_cart_item_id: existingItem.id,
            p_new_qty: existingItem.quantity + quantity,
          });
          if (updateError) throw updateError;
        } else {
          // Add new item with discounted variant price
          const insertData = {
            cart_id: activeCart.id,
            product_id: normalizedProductId,
            brand_id: productData.brand_id,
            quantity: quantity,
            price: finalPrice,
            variant_id: variantId // Store variant_id for stock deduction
          };
          
          // Add note if provided
          if (note) {
            insertData.note = note;
          }
          
          // Add custom fields to attributes if provided
          if (customFields && Object.keys(customFields).length > 0) {
            insertData.attributes = customFields;
          }

          const { error: insertError } = await supabase
            .from('cart_items')
            .insert(insertData);

          if (insertError) throw insertError;
        }

        // Refresh cart and items via RPC
        await fetchCartData();

        console.log('✅ Variant item added to cart with discount applied');
      } else {
        // Fetch product to get discount information
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('price, dis, brand_id, stock')
          .eq('id', normalizedProductId)
          .single();

        if (productError) {
          throw new Error(`Product not found: ${productError.message}`);
        }

        // Calculate discounted price if discount exists
        let finalPrice = parseFloat(productData.price || 0);
        if (productData.dis && productData.dis !== 'no') {
          const discountPercent = parseFloat(productData.dis);
          finalPrice = productData.price * (1 - discountPercent / 100);
          console.log(`💰 Discount applied: ${productData.dis}% → Price: ${productData.price} → ${finalPrice.toFixed(2)}`);
        }

        // Get or create cart
        const { data: cartData, error: cartError } = await supabase.rpc('cart_get');
        
        if (cartError) {
          console.error('❌ Cart RPC error:', cartError);
          throw new Error(`Failed to get cart: ${cartError.message}`);
        }
        
        console.log('📦 Cart RPC response:', cartData);
        
        let activeCart = cartData?.cart;

        // If cart is null, try to create it manually
        if (!activeCart || activeCart === 'null' || activeCart === null) {
          console.log('⚠️ No cart found, creating new cart...');
          
          // Try to create a cart directly
          const { data: newCart, error: createError } = await supabase
            .from('carts')
            .insert({
              profile_id: session.user.id,
              status: 'active',
              total: 0,
              item_count: 0
            })
            .select()
            .single();
          
          if (createError) {
            console.error('❌ Failed to create cart:', createError);
            throw new Error(`Failed to create cart: ${createError.message}`);
          }
          
          activeCart = newCart;
          console.log('✅ Cart created successfully:', activeCart.id);
        } else {
          console.log('✅ Cart retrieved successfully:', activeCart.id);
        }

        // Check if item already exists in cart
        const { items: currentItems } = await fetchCartData();
        const existingItem = currentItems.find(item => item.product_id === normalizedProductId);

        if (existingItem) {
          // Update quantity
          const { error: updateError } = await supabase.rpc('cart_update_quantity', {
            p_cart_item_id: existingItem.id,
            p_new_qty: existingItem.quantity + quantity,
          });
          if (updateError) throw updateError;
        } else {
          // Add new item with discounted price
          const insertData = {
            cart_id: activeCart.id,
            product_id: normalizedProductId,
            brand_id: productData.brand_id,
            quantity: quantity,
            price: finalPrice
          };
          
          // Add note if provided
          if (note) {
            insertData.note = note;
          }
          
          // Add custom fields to attributes if provided
          if (customFields && Object.keys(customFields).length > 0) {
            insertData.attributes = customFields;
          }

          const { error: insertError } = await supabase
            .from('cart_items')
            .insert(insertData);
          
          // If insert fails due to missing attributes column, try without it and store in localStorage
          if (insertError && insertError.message && insertError.message.includes('attributes')) {
            console.warn('cart_items.attributes column may not exist, storing custom fields in localStorage instead');
            delete insertData.attributes;
            const { error: retryError } = await supabase
              .from('cart_items')
              .insert(insertData);
            if (retryError) throw retryError;
            
            // Store custom fields in localStorage as fallback
            if (customFields && Object.keys(customFields).length > 0) {
              const { data: insertedItem } = await supabase
                .from('cart_items')
                .select('id')
                .eq('cart_id', activeCart.id)
                .eq('product_id', normalizedProductId)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();
              
              if (insertedItem) {
                localStorage.setItem(`cart_item_custom_fields_${insertedItem.id}`, JSON.stringify(customFields));
              }
            }
          } else if (insertError) {
            throw insertError;
          }

          if (insertError) throw insertError;
        }

        // Refresh cart and items via RPC
        await fetchCartData();

        console.log('✅ Item added to cart successfully with discount applied');
      }

      return true;
    } catch (err) {
      console.error('❌ Error adding to cart:', err);
      throw err;
    }
  };

  // Remove item from cart
  const removeFromCart = async (cartItemId) => {
    if (!session || !cart) {
      throw new Error('You must be logged in to remove items from cart');
    }

    try {
      console.log(`🗑️ Removing cart item: ${cartItemId}`);

      const { error: deleteError } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', cartItemId);

      if (deleteError) throw deleteError;

      // Refresh via RPC
      await fetchCartData();

      console.log('✅ Item removed from cart');
      return true;
    } catch (err) {
      console.error('❌ Error removing from cart:', err);
      throw err;
    }
  };

  // Update item quantity
  const updateQuantity = async (cartItemId, newQuantity) => {
    if (!session || !cart) {
      throw new Error('You must be logged in to update cart');
    }

    if (newQuantity < 1) {
      return removeFromCart(cartItemId);
    }

    try {
      console.log(`📝 Updating cart item via RPC ${cartItemId} → qty ${newQuantity}`);

      const { error: rpcError } = await supabase.rpc('cart_update_quantity', {
        p_cart_item_id: cartItemId,
        p_new_qty: newQuantity,
      });
      if (rpcError) throw rpcError;

      // Refresh via RPC
      await fetchCartData();

      console.log('✅ Quantity updated (RPC)');
      return true;
    } catch (err) {
      console.error('❌ Error updating quantity:', err);
      throw err;
    }
  };

  // Clear entire cart
  const clearCart = async () => {
    if (!session || !cart) {
      throw new Error('You must be logged in to clear cart');
    }

    try {
      console.log(`🗑️ Clearing all items from cart: ${cart.id}`);

      const { error: deleteError } = await supabase
        .from('cart_items')
        .delete()
        .eq('cart_id', cart.id);

      if (deleteError) throw deleteError;

      // Refresh via RPC
      await fetchCartData();

      console.log('✅ Cart cleared');
      return true;
    } catch (err) {
      console.error('❌ Error clearing cart:', err);
      throw err;
    }
  };

  // Calculate cart count
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return {
    cart,
    cartItems,
    loading,
    error,
    cartCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart
  };
};